/**
 * Enrichment fields that can be requested from the API.
 * Each value maps 1:1 to a GraphQL field name in the Fabric schema.
 */
export enum EnrichmentField {
  DescriptionInDutch = "DescriptionInDutch",
  DescriptionInFrench = "DescriptionInFrench",
  DescriptionInEnglish = "DescriptionInEnglish",
  DescriptionInGerman = "DescriptionInGerman",
  BrandCode = "BrandCode",
  BrandDescriptionInDutch = "BrandDescriptionInDutch",
  BrandDescriptionInFrench = "BrandDescriptionInFrench",
  CategoryCode = "CategoryCode",
  StatusName = "StatusName",
  StatusDescriptionInDutch = "StatusDescriptionInDutch",
  StatusDescriptionInFrench = "StatusDescriptionInFrench",
  ProductOwnerCode = "ProductOwnerCode",
  MinimumPurchaseQuantity = "MinimumPurchaseQuantity",
  PrimarySupplierId = "PrimarySupplierId",
  ThumbnailBinaryAsBase64 = "ThumbnailBinaryAsBase64",
}

/** User roles as defined in Entra ID. */
export enum AppRole {
  Sales = "Sales",
  Purchase = "Purchase",
}

/** Whether the input column contains article codes or barcodes. */
export type LookupType = "ArticleCode" | "Barcode";

/** Fields shared by both Sales and Purchase roles. */
const SHARED_FIELDS: EnrichmentField[] = [
  EnrichmentField.DescriptionInDutch,
  EnrichmentField.DescriptionInFrench,
  EnrichmentField.DescriptionInEnglish,
  EnrichmentField.DescriptionInGerman,
  EnrichmentField.BrandCode,
  EnrichmentField.BrandDescriptionInDutch,
  EnrichmentField.BrandDescriptionInFrench,
  EnrichmentField.CategoryCode,
  EnrichmentField.StatusName,
  EnrichmentField.StatusDescriptionInDutch,
  EnrichmentField.StatusDescriptionInFrench,
  EnrichmentField.ProductOwnerCode,
];

/** Fields available per role. */
export const FIELDS_BY_ROLE: Record<AppRole, EnrichmentField[]> = {
  [AppRole.Sales]: [
    ...SHARED_FIELDS,
    EnrichmentField.ThumbnailBinaryAsBase64,
  ],
  [AppRole.Purchase]: [
    ...SHARED_FIELDS,
    EnrichmentField.MinimumPurchaseQuantity,
    EnrichmentField.PrimarySupplierId,
    EnrichmentField.ThumbnailBinaryAsBase64,
  ],
};

/** Human-readable labels for enrichment fields. */
export const FIELD_LABELS: Record<EnrichmentField, string> = {
  [EnrichmentField.DescriptionInDutch]: "Description (Dutch)",
  [EnrichmentField.DescriptionInFrench]: "Description (French)",
  [EnrichmentField.DescriptionInEnglish]: "Description (English)",
  [EnrichmentField.DescriptionInGerman]: "Description (German)",
  [EnrichmentField.BrandCode]: "Brand Code",
  [EnrichmentField.BrandDescriptionInDutch]: "Brand (Dutch)",
  [EnrichmentField.BrandDescriptionInFrench]: "Brand (French)",
  [EnrichmentField.CategoryCode]: "Category",
  [EnrichmentField.StatusName]: "Status",
  [EnrichmentField.StatusDescriptionInDutch]: "Status (Dutch)",
  [EnrichmentField.StatusDescriptionInFrench]: "Status (French)",
  [EnrichmentField.ProductOwnerCode]: "Product Owner",
  [EnrichmentField.MinimumPurchaseQuantity]: "Min. Purchase Qty",
  [EnrichmentField.PrimarySupplierId]: "Primary Supplier",
  [EnrichmentField.ThumbnailBinaryAsBase64]: "Thumbnail",
};

/**
 * Maps an EnrichmentField to the corresponding GraphQL field name.
 */
export function fieldToGraphQL(field: EnrichmentField): string {
  switch (field) {
    case EnrichmentField.PrimarySupplierId:
      return "_PrimarySupplierId";
    default:
      // All other fields have the same name in GraphQL
      return field as string;
  }
}

/** Maps an EnrichmentField to the corresponding camelCase property key on ArticleResult. */
export const FIELD_TO_PROP: Record<EnrichmentField, keyof ArticleResult | undefined> = {
  [EnrichmentField.DescriptionInDutch]: "descriptionInDutch",
  [EnrichmentField.DescriptionInFrench]: "descriptionInFrench",
  [EnrichmentField.DescriptionInEnglish]: "descriptionInEnglish",
  [EnrichmentField.DescriptionInGerman]: "descriptionInGerman",
  [EnrichmentField.BrandCode]: "brandCode",
  [EnrichmentField.BrandDescriptionInDutch]: "brandDescriptionInDutch",
  [EnrichmentField.BrandDescriptionInFrench]: "brandDescriptionInFrench",
  [EnrichmentField.CategoryCode]: "categoryCode",
  [EnrichmentField.StatusName]: "statusName",
  [EnrichmentField.StatusDescriptionInDutch]: "statusDescriptionInDutch",
  [EnrichmentField.StatusDescriptionInFrench]: "statusDescriptionInFrench",
  [EnrichmentField.ProductOwnerCode]: "productOwnerCode",
  [EnrichmentField.MinimumPurchaseQuantity]: "minimumPurchaseQuantity",
  [EnrichmentField.PrimarySupplierId]: "primarySupplierId",
  [EnrichmentField.ThumbnailBinaryAsBase64]: "thumbnail",
};

/** Thumbnail binary data returned by the API. */
export interface ThumbnailData {
  base64: string;
  mimeType: string;
}

/** Enrichment result for a single article. */
export interface ArticleResult {
  articleCode: string;
  barcode?: string;
  descriptionInDutch?: string;
  descriptionInFrench?: string;
  descriptionInEnglish?: string;
  descriptionInGerman?: string;
  brandCode?: string;
  brandDescriptionInDutch?: string;
  brandDescriptionInFrench?: string;
  categoryCode?: string;
  statusName?: string;
  statusDescriptionInDutch?: string;
  statusDescriptionInFrench?: string;
  productOwnerCode?: string;
  minimumPurchaseQuantity?: number;
  primarySupplierId?: string;
  thumbnail?: ThumbnailData;
}

/** Request payload sent to the API. */
export interface EnrichmentRequest {
  codes: string[];
  fields: EnrichmentField[];
  role: AppRole;
  lookupType: LookupType;
}

/** Response from the API. */
export interface EnrichmentResponse {
  articles: ArticleResult[];
  notFound: string[];
  errors: ProcessingError[];
}

/** Error for a specific article during processing. */
export interface ProcessingError {
  articleCode: string;
  message: string;
}

/** User configuration for the enrichment task pane form. */
export interface EnrichmentConfig {
  startRow: number;
  endRow: number;
  articleCodeColumn: string;
  lookupType: LookupType;
  selectedFields: EnrichmentField[];
  fieldColumnMapping: Record<EnrichmentField, string>;
  thumbnailSize: number;
  fitToCell: boolean;
  noChangeIfNoResults: boolean;
}
